import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Grid, Typography } from '@mui/material';
import React from 'react';
import {
  LevelBarProps,
  LineChartProps,
  RootineBarChart,
  RootineLevelBar,
  RootineLineChart,
} from '../../../atoms';
import { AnalyteReport, UserNutrientSummary } from '../../../types';
import { useGlobalState } from '../../../utils/GlobalStateProvider';

export interface AnalyteDetailsProps {
  analyte: AnalyteReport;
}

export const AnalyteDetails = (props: AnalyteDetailsProps): JSX.Element => {
  const context = useGlobalState(true);

  const sectionHeading = {
    fontSize: 14,
    opacity: 0.5,
  };

  const sectionInfo = {
    fontSize: 16,
    fontWeight: 'normal',
  };

  const getNutrientsImpacted = (): UserNutrientSummary[] => {
    if (context.state.userNutrients?.length > 0) {
      return context.state.userNutrients.filter((nutrient) =>
        props?.analyte.nutrients_impacted.split(', ').includes(nutrient.title!)
      );
    }
    return [];
  };

  const chartProps: LineChartProps = {
    data: props?.analyte.result_history
      .filter((result) => result.date)
      .map((result) => {
        return {
          date: Date.parse(result.date!), // epoch date
          result: result.result,
        };
      }),
    optimalMax: props.analyte.optimal_max_value,
    optimalMin: props.analyte.optimal_min_value,
  };

  const levelBarProps: LevelBarProps = {
    level: props.analyte.result_history[0].result,
    optimalMax: props.analyte.optimal_max_value,
    optimalMin: props.analyte.optimal_min_value,
    detail: true,
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        spacing={2}
        rowSpacing={2}
        sx={{
          paddingLeft: { xs: '16px' },
          paddingRight: { xs: '16px' },
          paddingTop: { xs: '16px' },
          marginTop: { xs: '10', md: '10' },
        }}
      >
        <Grid item xs={12}>
          <Typography sx={sectionHeading}>{'BLOOD NUTRIENT LEVEL'}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={{ fontSize: { xs: 48, md: 60 } }}>
            {props?.analyte.name}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <RootineLevelBar {...levelBarProps} />
        </Grid>
        <Grid item xs={12}>
          <Typography sx={sectionHeading}>{'RESULTS'}</Typography>
        </Grid>
        <Grid item container xs={12} sx={{ padding: '16px' }}>
          <Grid item xs={6}>
            <Typography color="text.secondary" sx={sectionInfo}>
              {'LOW'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="right" sx={sectionInfo}>
              {props?.analyte.optimal_min_value === 0
                ? 'none'
                : `Less than ${props?.analyte.optimal_min_value}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography color="text.secondary" sx={sectionInfo}>
              {'OPTIMAL'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="right" sx={sectionInfo}>
              {props?.analyte.optimal_min_value} -{' '}
              {props?.analyte.optimal_max_value}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography color="text.secondary" sx={sectionInfo}>
              {'HIGH'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              align="right"
              sx={sectionInfo}
            >{`Greater than ${props?.analyte.optimal_max_value}`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={sectionHeading}>
            {'IMPORTANCE OF TESTING THIS ANALYTE'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={sectionInfo}>
            {props.analyte.description.split('http')[0]}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={sectionHeading}>{'TEST'}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={sectionInfo}>{`Blood ${
            props?.analyte.test_type.charAt(0).toUpperCase() +
            props?.analyte.test_type.slice(1)
          }`}</Typography>
        </Grid>
        {getNutrientsImpacted().length > 0 && (
          <Grid item xs={12}>
            <Typography sx={sectionHeading}>
              {'HOW THIS GUIDES DOSAGE'}
            </Typography>
          </Grid>
        )}

        {getNutrientsImpacted().map((nutrient) => {
          return (
            <Grid
              key={nutrient.nutrient_id}
              item
              container
              xs={12}
              sx={{ fontSize: 16 }}
            >
              <Grid item xs={6}>
                <Typography sx={sectionInfo}>{nutrient.title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right" color="info.main" sx={sectionInfo}>
                  {nutrient.value_mg}
                </Typography>
              </Grid>
            </Grid>
          );
        })}
        {props.analyte.result_history[0].date && (
          <>
            <Grid container item xs={12} sx={{ alignItems: 'center' }}>
              <SignalCellularAltIcon fontSize="small" sx={{ opacity: 0.5 }} />
              <Typography sx={sectionHeading}>{' HISTORY'}</Typography>
            </Grid>
            <Grid item xs={12}>
              {chartProps.data && chartProps.data.length > 1 ? (
                <RootineLineChart {...chartProps} />
              ) : (
                <RootineBarChart {...chartProps} />
              )}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};
